import { FileTextOutlined } from '@ant-design/icons';
import type { TermsAcceptanceStatus } from '@context/auth.context';
import useAuth from '@hooks/use-auth';
import type { ActionResponse } from '@services/action-request';
import { isActionResponse } from '@services/action-request';
import { Alert, Button, Card, Checkbox, Form, Typography } from 'antd';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useFetcher, useLocation } from 'react-router-dom';

import { ACTIONS } from './action';
import styles from './TermsAcknowledgement.module.scss';

interface FormValues {
  accepted: boolean;
}

const DEFAULT_TERMS_URL = 'https://supportontelegram.com/terms';

function isSafeReturnPath(value: string | null): value is string {
  return (
    value !== null && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/terms-acknowledgement')
  );
}

function getReturnPath(returnUrl: unknown) {
  if (returnUrl && typeof returnUrl === 'object' && 'pathname' in returnUrl) {
    const { pathname, search, hash } = returnUrl as Partial<Location>;
    const path = `${pathname ?? '/'}${search ?? ''}${hash ?? ''}`;

    return isSafeReturnPath(path) ? path : '/';
  }

  return '/';
}

const TermsAcknowledgement: FC = () => {
  const { getTermsAcceptanceStatus, isAuthenticated, requiresTermsAcceptance, updateTermsAcceptance } = useAuth();
  const location = useLocation();
  const fetcher = useFetcher<TermsAcceptanceStatus | ActionResponse>();
  const [form] = Form.useForm<FormValues>();
  const [error, setError] = useState<string>();
  const status = getTermsAcceptanceStatus();
  const returnPath = getReturnPath(location.state?.returnUrl);
  const isSubmitting = fetcher.state !== 'idle';
  const isAgentAcknowledgement = status?.source === 'agent_login';
  const termsAcceptanceRequired = requiresTermsAcceptance();

  const copy = {
    title: isAgentAcknowledgement ? 'Acknowledge Acceptable Use Rules' : 'Accept Terms of Service',
    description: isAgentAcknowledgement
      ? 'Acknowledgement is required before you can send messages or continue using the platform.'
      : 'Acceptance is required before you can continue using the platform.',
    checkbox: isAgentAcknowledgement
      ? 'I agree to follow the SupportOnTelegram Terms of Service, including the Acceptable Use rules, and to use the platform only for authorized customer support activity.'
      : 'I have read and agree to the SupportOnTelegram Terms of Service.',
    button: isAgentAcknowledgement ? 'Acknowledge and continue' : 'Accept and continue',
  };

  useEffect(() => {
    if (!fetcher.data) {
      return;
    }

    if (isActionResponse(fetcher.data)) {
      setError(fetcher.data.statusText || 'Could not record your acknowledgement. Please try again.');
      return;
    }

    updateTermsAcceptance(fetcher.data);
  }, [fetcher.data, updateTermsAcceptance]);

  const onFinish = useCallback(() => {
    setError(undefined);
    fetcher.submit(
      {
        intent: ACTIONS.AcceptTerms,
      },
      {
        method: 'POST',
        encType: 'application/json',
      },
    );
  }, [fetcher]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ returnUrl: location }} replace />;
  }

  if (!termsAcceptanceRequired) {
    return <Navigate to={returnPath} replace />;
  }

  return (
    <main className={styles.page}>
      <Card className={styles.panel}>
        <Typography.Title level={2}>{copy.title}</Typography.Title>
        <Typography.Paragraph className={styles.description}>{copy.description}</Typography.Paragraph>
        <Button
          className={styles.termsButton}
          href={status?.terms_url ?? DEFAULT_TERMS_URL}
          icon={<FileTextOutlined />}
          target="_blank"
          rel="noopener noreferrer"
          size="large"
        >
          Open Terms of Service
        </Button>
        {error && <Alert className={styles.error} message={error} type="error" showIcon />}
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="accepted"
            valuePropName="checked"
            rules={[
              {
                validator: async (_, value: boolean | undefined) => {
                  if (value) {
                    return;
                  }

                  throw new Error('Please confirm before continuing.');
                },
              },
            ]}
          >
            <Checkbox disabled={isSubmitting}>{copy.checkbox}</Checkbox>
          </Form.Item>
          <Form.Item className={styles.actions} shouldUpdate>
            {() => (
              <Button
                type="primary"
                htmlType="submit"
                block={true}
                loading={isSubmitting}
                disabled={!form.getFieldValue('accepted') || isSubmitting}
              >
                {copy.button}
              </Button>
            )}
          </Form.Item>
        </Form>
      </Card>
    </main>
  );
};

export default TermsAcknowledgement;
